import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Import the local background image (adjust path as needed)
import spaceBg from '../assets/bg.gif';

const Skills = () => {
  const skills = [
    { name: 'Python', level: 90, icon: '🐍', color: '#3776AB' },
    { name: 'Java', level: 80, icon: '☕', color: '#007396' },
    { name: 'CSS/Tailwind', level: 88, icon: '🎨', color: '#38B2AC' },
    { name: 'React.js', level: 90, icon: '⚛️', color: '#61DAFB' },
    { name: 'Node.js', level: 85, icon: '🚀', color: '#339933' },
    { name: 'MongoDB', level: 88, icon: '🍃', color: '#47A248' },
    { name: 'React Native', level: 75, icon: '📱', color: '#61DAFB' },
    { name: 'Firebase', level: 78, icon: '🔥', color: '#FFCA28' },
    { name: 'Flutter', level: 65, icon: '🦋', color: '#02569B' },
    { name: 'Git', level: 85, icon: '🔀', color: '#F05032' },
    { name: 'Docker', level: 70, icon: '🐳', color: '#2496ED' },
    { name: 'AWS', level: 65, icon: '☁️', color: '#FF9900' },
  ];

  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('frontend');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const categories = {
    all: 'All Skills',
    frontend: 'Frontend',
    backend: 'Backend',
    mobile: 'Mobile',
    tools: 'Tools',
  };

  const getSkillCategory = (skillName) => {
    const frontend = ['CSS/Tailwind', 'React.js'];
    const backend = ['Python', 'Java', 'Node.js', 'MongoDB', 'Firebase', 'AWS'];
    const mobile = ['React Native', 'Flutter'];
    const tools = ['Git', 'Docker'];

    if (frontend.includes(skillName)) return 'frontend';
    if (backend.includes(skillName)) return 'backend';
    if (mobile.includes(skillName)) return 'mobile';
    if (tools.includes(skillName)) return 'tools';
    return 'other';
  };

  const filteredSkills =
    activeCategory === 'all'
      ? skills
      : skills.filter((skill) => getSkillCategory(skill.name) === activeCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.2,
        delay: 0.5,
      },
    },
  };

  const itemVariants = {
    hidden: {
      y: 100,
      opacity: 0,
      scale: 0.5,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        type: 'spring',
        stiffness: 80,
        damping: 20,
      },
    },
    hover: {
      scale: 1.15,
      rotate: 3,
      boxShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
      transition: {
        duration: 0.4,
        type: 'spring',
        stiffness: 200,
      },
    },
    orbit: {
      x: [0, 8, -8, 0],
      y: [0, -10, 10, 0],
      rotate: [0, 3, -3, 0],
      transition: {
        duration: 7,
        ease: 'easeInOut',
        repeat: Infinity,
        delay: (index) => index * 0.4,
      },
    },
  };

  const iconVariants = {
    visible: {
      scale: [1, 1.4, 1],
      rotate: [0, 10, -10, 0],
      transition: {
        duration: 1,
        ease: 'easeOut',
        delay: 0.3,
      },
    },
    hover: {
      scale: 1.6,
      rotate: [0, 360],
      transition: {
        duration: 0.7,
        type: 'spring',
        stiffness: 150,
      },
    },
  };

  return (
    <motion.section
      id="skills"
      className="scroll-reveal"
      style={{
        background: `url(${spaceBg}) no-repeat center center/cover, radial-gradient(circle at center, #0f0f1a 0%, #050505 70%)`,
        backgroundBlendMode: 'overlay',
        opacity: 0.3, // Subtle background image opacity
        padding: '100px 0',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 1 }}
    >
      {/* Starry Background */}
      {[...Array(40)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: `${Math.random() * 2 + 1}px`,
            height: `${Math.random() * 2 + 1}px`,
            background: 'rgba(200, 200, 255, 0.5)',
            borderRadius: '50%',
            zIndex: 0,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            boxShadow: '0 0 5px rgba(200, 200, 255, 0.3)',
          }}
          animate={{
            opacity: [0.1, 0.5, 0.1],
            scale: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: Math.random() * 3,
          }}
        />
      ))}

      {/* Nebula Effect */}
      <motion.div
        style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(50, 50, 100, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
          borderRadius: '50%',
          top: '10%',
          left: '15%',
          zIndex: 0,
          filter: 'blur(60px)',
        }}
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          background:
            'radial-gradient(circle, rgba(100, 50, 100, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
          borderRadius: '50%',
          bottom: '10%',
          right: '10%',
          zIndex: 0,
          filter: 'blur(70px)',
        }}
        animate={{
          scale: ['linear'],
          opacity: [0, 0.7, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'linear',
          delay: Math.random() * 6,
        }}
      />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mb-16"
        >
          <motion.h2
            className="section-title"
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: '900',
              marginBottom: '1.5rem',
              color: '#A0A0CC',
              textShadow: '0 0 15px rgba(100, 100, 255, 0.3)',
              position: 'relative',
            }}
          >
            <motion.span
              style={{
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '5px',
                background: 'linear-gradient(90deg, #3b3b7d, #2a2a5e)',
                borderRadius: '3px',
                boxShadow: '0 0 10px rgba(100, 100, 255, 0.4)',
              }}
              animate={{
                width: [100, 110, 100],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
              <motion.span
                role="img"
                aria-label="skills"
                animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                transition={{ duration: 3, ease: 'easeInOut', repeat: Infinity }}
              >
                
              </motion.span>
              Technologies.
            </span>
          </motion.h2>

          <motion.p
            style={{
              fontSize: '1.25rem',
              color: '#B0B0E0',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: '1.7',
              textShadow: '0 0 8px rgba(200, 200, 255, 0.2)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            
          </motion.p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            marginBottom: '3rem',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {Object.entries(categories).map(([key, label]) => (
            <motion.button
              key={key}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '50px',
                fontWeight: '700',
                fontSize: '1rem',
                cursor: 'pointer',
                border: '2px solid rgba(100, 100, 255, 0.2)',
                background: activeCategory === key
                  ? 'linear-gradient(90deg, #3b3b7d, #5c5cb8)'
                  : 'rgba(10, 10, 20, 0.9)',
                color: activeCategory === key ? '#e0e0ff' : '#A0A0CC',
                boxShadow: activeCategory === key
                  ? '0 0 15px rgba(100, 100, 255, 0.5)'
                  : '0 0 5px rgba(100, 100, 255, 0.2)',
                position: 'relative',
                overflow: 'hidden',
                textShadow: '0 0 5px rgba(200, 200, 255, 0.3)',
              }}
              whileHover={{
                scale: 1.1,
                boxShadow: '0 0 20px rgba(100, 100, 255, 0.4)',
                borderColor: '#5c5cb8',
              }}
              whileTap={{
                scale: 0.95,
                boxShadow: '0 0 25px rgba(100, 100, 255, 0.6)',
              }}
              onClick={() => setActiveCategory(key)}
            >
              {label}
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, rgba(200,200,255,0.2), rgba(200,200,255,0))',
                  opacity: 0,
                }}
                animate={{ x: ['-100%', '100%'], opacity: [0, 0.7, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.button>
          ))}
        </motion.div>

        <motion.div
          className="skills-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 240px))',
            gap: '2rem',
            justifyContent: 'center',
            margin: '0 auto',
            padding: '0 20px',
            position: 'relative',
          }}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredSkills.map((skill, index) => (
            <motion.div
              key={index}
              className="skill-card"
              style={{
                position: 'relative',
                padding: '2rem',
                borderRadius: '1.5rem',
                background: 'linear-gradient(135deg, rgba(10, 10, 20, 0.95), rgba(5, 5, 10, 0.9))',
                boxShadow: '0 0 20px rgba(0, 0, 0, 0.9), inset 0 0 8px rgba(100, 100, 255, 0.1)',
                minHeight: '180px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                overflow: 'hidden',
                border: '2px solid rgba(50, 50, 100, 0.3)',
              }}
              variants={itemVariants}
              initial="hidden"
              animate={['visible', 'orbit']}
              whileHover="hover"
              custom={index}
              onMouseEnter={() => setHoveredSkill(index)}
              onMouseLeave={() => setHoveredSkill(null)}
            >
              <motion.div
                style={{
                  fontSize: '3rem',
                  marginBottom: '1rem',
                  textShadow: `0 0 10px ${skill.color}80`,
                }}
                variants={iconVariants}
                animate={hoveredSkill === index ? 'hover' : 'visible'}
              >
                {skill.icon}
              </motion.div>

              <motion.h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#B0B0E0',
                  textAlign: 'center',
                  marginBottom: '1.5rem',
                  zIndex: 2,
                  textShadow: '0 0 8px rgba(200, 200, 255, 0.3)',
                }}
                animate={hoveredSkill === index ? { scale: 1.15 } : { scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                {skill.name}
              </motion.h3>

              <div
                style={{
                  width: '100%',
                  height: '12px',
                  background: 'rgba(5, 5, 10, 0.95)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  zIndex: 2,
                  boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.7)',
                }}
              >
                <motion.div
                  style={{
                    height: '100%',
                    background: `linear-gradient(90deg, ${skill.color}80, ${skill.color}40)`,
                    borderRadius: '6px',
                    boxShadow: `0 0 10px ${skill.color}60`,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  initial={{ width: 0 }}
                  animate={{
                    width: `${skill.level}%`,
                    transition: {
                      duration: 2,
                      delay: index * 0.2 + 0.5,
                      ease: 'easeInOut',
                    },
                  }}
                >
                  <motion.div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(90deg, ${skill.color}00, ${skill.color}40, ${skill.color}00)`,
                      opacity: 0,
                    }}
                    animate={{
                      opacity: [0, 0.5, 0],
                      x: ['-100%', '100%'],
                    }}
                    transition={{
                      duration: 1.5,
                      delay: index * 0.2 + 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                </motion.div>
              </div>

              <AnimatePresence>
                {hoveredSkill === index && (
                  <motion.div
                    style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'linear-gradient(90deg, #3b3b7d, #5c5cb8)',
                      color: '#e0e0ff',
                      fontSize: '1rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '1rem',
                      boxShadow: '0 0 15px rgba(100, 100, 255, 0.4)',
                      zIndex: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      border: '1px solid rgba(100, 100, 255, 0.3)',
                    }}
                    initial={{ opacity: 0, y: 20, scale: 0.7 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.7 }}
                    transition={{ duration: 0.4, type: 'spring' }}
                  >
                    <motion.span
                      style={{ fontWeight: '800' }}
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity }}
                    >
                      {skill.level}%
                    </motion.span>
                    <span>Star Power</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Cosmic Glow */}
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: `radial-gradient(circle at center, ${skill.color}05, transparent 70%)`,
                  zIndex: 1,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: hoveredSkill === index ? 0.6 : 0 }}
                transition={{ duration: 0.5 }}
              />

              {/* Pulsating Star Effect */}
              <motion.div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '150px',
                  height: '150px',
                  background: `radial-gradient(circle, ${skill.color}20, transparent 70%)`,
                  borderRadius: '50%',
                  filter: 'blur(20px)',
                  zIndex: 0,
                  transform: 'translate(-50%, -50%)',
                }}
                animate={{
                  scale: hoveredSkill === index ? [1, 1.4, 1] : 1,
                  opacity: hoveredSkill === index ? [0.1, 0.5, 0.1] : 0.1,
                }}
                transition={{
                  duration: 1.5,
                  ease: 'easeOut',
                  repeat: Infinity,
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style jsx>{`
        @keyframes pulseSlow {
          0%, 100% {
            transform: scale(1);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.25;
          }
        }

        @keyframes nebulaGlow {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.35;
            transform: scale(1.1);
          }
        }

        @keyframes cometTrail {
          0% {
            opacity: 0;
            transform: translateX(-100%) translateY(0);
          }
          50% {
            opacity: 0.7;
          }
          100% {
            opacity: 0;
            transform: translateX(200%) translateY(-250px);
          }
        }

        .skill-card {
          transition: all 0.3s ease;
        }

        .skill-card:hover {
          transform: translateZ(15px);
        }

        @media (max-width: 768px) {
          .skills-grid {
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) !important;
          }
        }

        @media (max-width: 480px) {
          .skills-grid {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)) !important;
            gap: 1.5rem !important;
          }

          .skill-card {
            padding: 1.5rem !important;
            min-height: 160px !important;
          }
        }
      `}</style>
    </motion.section>
  );
};

export default Skills;